import { IBaseResource } from "../api.types";
import { IIndustry } from "../industries/industries.types";
import type { IOrganizationGroupMinimal } from "../organization-groups";
import { IUser } from "../users";

export interface IOrganizationBase {
    name: string;
    logo_url?: string;
}

export interface IOrganizationCreate extends IOrganizationBase {
    industry_id?: number;
}

export interface IOrganizationUpdate extends IOrganizationBase {
    industry_id?: number | null;
}

export interface IOrganization extends IOrganizationBase, IBaseResource {
    industry_id?: number | null;
    industry?: IIndustry | null;
    group?: IOrganizationGroupMinimal | null;
}

export interface IOrganizationMinimal {
    id: number;
    name: string;
}

export interface IOrganizationMember extends IBaseResource {
    user_id: number;
    organization_id: number;
}

export interface IOrganizationMemberExtended extends IOrganizationMember {
    user: IUser;
}

export interface IOrganizationMemberCreate {
    user_ids: number[];
}

export interface IOrganizationStats {
    scopes: number;
    pages: number;
    clauses: number;
}

export interface IOrganizationLogoUpload {
    logo_url: string;
}

export interface ISignedOrganizationLogo {
    signed_url: string;
}

export interface IOrganizationSettingsBase {
    field_extraction_enabled: boolean;
    ai_clause_comparison_enabled: boolean;
    ai_chat_enabled: boolean;
    scope_reasoning_enabled: boolean;
    context_enrichment_enabled: boolean;
    clause_matching_enabled: boolean;
    output_templates_enabled: boolean;
    policy_primer_enabled: boolean;
    reports_enabled?: boolean;
    scopes_enabled?: boolean;
    chat_personalization?: string | null;
    share_polly_enabled: boolean;
    allowed_domains: string[];
}

export interface IOrganizationSettings extends IOrganizationSettingsBase {
    organization_id: number;
    /**
     * Count of pending Path B shares cancelled by an allowlist tighten on the
     * same PUT. Default 0; non-zero ONLY on a tighten response. The admin UI
     * surfaces the number in a toast when > 0 so operators see the auto-cancel
     * side-effect of an allowlist edit (S11 + S6 contract).
     */
    cancelled_invitations?: number;
}

export interface IOrganizationSettingsUpdate extends IOrganizationSettingsBase {}

/**
 * Customer-safe view of org settings, mirroring the BE
 * `OrganizationSettingsCustomer` schema served by /v1/organizations/context.
 * Drops `allowed_domains` and `chat_personalization` (admin-only context).
 * Add new flags here only when they are safe to expose to every authenticated
 * org member.
 */
export interface IOrganizationSettingsCustomer {
    field_extraction_enabled: boolean;
    ai_clause_comparison_enabled: boolean;
    ai_chat_enabled: boolean;
    scope_reasoning_enabled: boolean;
    context_enrichment_enabled: boolean;
    share_polly_enabled: boolean;
    output_templates_enabled: boolean;
    reports_enabled?: boolean;
    scopes_enabled?: boolean;
}

/** The settings a current API stores and serves; doubles and fixtures satisfy these. */
export type StoredOrganizationSettings = IOrganizationSettings & {
    reports_enabled: boolean;
    scopes_enabled: boolean;
};

export type StoredOrganizationSettingsCustomer =
    IOrganizationSettingsCustomer & {
        reports_enabled: boolean;
        scopes_enabled: boolean;
    };

/** Whether reports are on; a payload from an API that predates the flag reads as off. */
export function readReportsEnabled(
    settings: { reports_enabled?: boolean } | null | undefined
): boolean {
    return settings?.reports_enabled === true;
}

/** Whether scopes are on; a payload from an API that predates the flag reads as enabled. */
export function readScopesEnabled(
    settings: { scopes_enabled?: boolean } | null | undefined
): boolean {
    return settings?.scopes_enabled !== false;
}

export interface IOrganizationContextResponse {
    has_organization_context: boolean;
    chat_max_direct_documents: number;
    organization?: IOrganization;
    organization_settings?: IOrganizationSettingsCustomer;
}
